/**
 * platform-nav.ts — Danh sách menu Platform Console (thuần, không đụng React/store) — tách riêng khỏi
 * platform-layout.tsx để `currentPageLabel` test được (platform-layout.tsx kéo theo ui-store.ts, đọc
 * `window.matchMedia` ngay lúc import module → vỡ môi trường test không có DOM).
 */
import {
  LayoutDashboard, Building2, Radio, Tags, TicketPercent, FileText, FileSpreadsheet, MessageSquareHeart,
  Palette, Megaphone, Globe, BellRing, UserCog, ScrollText,
  Sparkles, KeyRound, Boxes, SlidersHorizontal,
  Handshake, Users, Link2, Coins, Wallet, Receipt, Settings2,
  type LucideIcon,
} from 'lucide-react'

export interface NavItem { label: string; to: string; icon: LucideIcon; end: boolean }

export const PLATFORM_NAV: NavItem[] = [
  { label: 'Tổng quan', to: '/platform', icon: LayoutDashboard, end: true },
  { label: 'Tổ chức', to: '/platform/companies', icon: Building2, end: false },
  { label: 'Sức khoẻ kênh', to: '/platform/channels', icon: Radio, end: false },
  { label: 'Bảng giá gói', to: '/platform/pricing', icon: Tags, end: false },
  { label: 'Mã ưu đãi', to: '/platform/promo-codes', icon: TicketPercent, end: false },
  { label: 'Hoá đơn điện tử', to: '/platform/invoices', icon: FileText, end: false },
  { label: 'Báo giá thủ công', to: '/platform/manual-quotes', icon: FileSpreadsheet, end: false },
  { label: 'Góp ý của khách', to: '/platform/feedback', icon: MessageSquareHeart, end: false },
]

/**
 * Nhóm "Cài đặt" — mọi thứ thuộc cấu hình hệ thống chung đặt ở đây
 * (sau này thêm mục hệ thống mới cũng đưa vào nhóm này).
 */
export const SETTINGS_NAV: NavItem[] = [
  { label: 'Thương hiệu', to: '/platform/branding', icon: Palette, end: false },
  { label: 'Thông báo cập nhật', to: '/platform/release-notes', icon: Megaphone, end: false },
  { label: 'Trang giới thiệu', to: '/platform/landing', icon: Globe, end: false },
  { label: 'Thông báo Lark', to: '/platform/lark', icon: BellRing, end: false },
  { label: 'Super-admin', to: '/platform/admins', icon: UserCog, end: false },
  { label: 'Nhật ký', to: '/platform/audit', icon: ScrollText, end: false },
]

// Nhóm "AI" — AI hai chế độ (AI của eCDP trừ credit / Key của tôi). docs/AI-2-CHE-DO-HOP-DONG-API.md.
export const AI_NAV: NavItem[] = [
  { label: 'Usage & chi phí AI', to: '/platform/ai', icon: Sparkles, end: true },
  { label: 'Khoá API của eCDP', to: '/platform/ai/keys', icon: KeyRound, end: false },
  { label: 'Danh mục model AI', to: '/platform/ai/catalog', icon: Boxes, end: false },
  { label: 'AI theo tổ chức', to: '/platform/ai/orgs', icon: Building2, end: false },
  { label: 'Cài đặt token', to: '/platform/ai/credit-settings', icon: SlidersHorizontal, end: false },
]

// Nhóm "Tiếp thị liên kết" (thêm cuối menu) — modules/affiliate.
export const AFFILIATE_NAV: NavItem[] = [
  { label: 'Tổng quan', to: '/platform/affiliate', icon: Handshake, end: true },
  { label: 'Đối tác', to: '/platform/affiliate/partners', icon: Users, end: false },
  { label: 'Khách & lead', to: '/platform/affiliate/referrals', icon: Link2, end: false },
  { label: 'Hoa hồng', to: '/platform/affiliate/commissions', icon: Coins, end: false },
  { label: 'Lệnh rút tiền', to: '/platform/affiliate/withdrawals', icon: Wallet, end: false },
  { label: 'Thuế TNCN', to: '/platform/affiliate/tax', icon: Receipt, end: false },
  { label: 'Cài đặt chương trình', to: '/platform/affiliate/settings', icon: Settings2, end: false },
]

/** Toàn bộ danh sách menu, dùng để tra nhãn trang hiện tại cho đầu trang điện thoại. */
export const ALL_NAV: NavItem[] = [...PLATFORM_NAV, ...AI_NAV, ...AFFILIATE_NAV, ...SETTINGS_NAV]

/** Nhãn trang hiện tại (đầu trang điện thoại) — khớp dài nhất trong mọi mục menu, ưu tiên mục `end` khớp đúng. */
export function currentPageLabel(pathname: string): string {
  const exact = ALL_NAV.find((i) => i.end && i.to === pathname)
  if (exact) return exact.label
  const prefixMatches = ALL_NAV.filter((i) => !i.end && pathname.startsWith(i.to))
  const best = prefixMatches.sort((a, b) => b.to.length - a.to.length)[0]
  return best?.label ?? 'Platform Console'
}
